import React, { useState } from "react";
import { Loader2, PauseCircle, Users, ArrowDownCircle, MessageCircle, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const REASONS = [
  ["too_expensive", "Too expensive"],
  ["missing_feature", "Missing a feature we need"],
  ["project_finished", "The project or programme finished"],
  ["switching", "Moving to another tool"],
  ["not_using", "Not using it enough"],
  ["other", "Something else"],
];

// What we offer, per reason. Offering a discount to somebody whose project simply ended is noise.
const OFFERS = {
  too_expensive: ["seats", "downgrade", "pause"],
  missing_feature: ["talk"],
  project_finished: ["pause"],
  switching: ["talk", "pause"],
  not_using: ["pause", "talk"],
  other: ["pause", "talk"],
};

const OFFER_COPY = {
  pause: {
    icon: PauseCircle, title: "Pause instead",
    body: "Billing stops and restarts automatically. Your projects, plans and history stay exactly as they are, and the workspace stays fully editable throughout.",
  },
  seats: {
    icon: Users, title: "Pay for fewer people",
    body: "Drop to only the seats you are actually using. It takes effect immediately and is prorated.",
  },
  downgrade: {
    icon: ArrowDownCircle, title: "Move down to Foundation",
    body: "Keep the registers, milestones and reporting your team uses every week at a lower price per person.",
  },
  talk: {
    icon: MessageCircle, title: "Talk to a person first",
    body: "Tell us what is missing or what went wrong. If it is something we can fix or already have, you will know within a working day.",
  },
};

export const CancelFlow = ({ current, onDone }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [feedbackId, setFeedbackId] = useState(null);
  const [maxMonths, setMaxMonths] = useState(3);
  const [busy, setBusy] = useState(null);

  const reset = () => { setStep(1); setReason(""); setComment(""); setFeedbackId(null); };

  const submitReason = async () => {
    setBusy("reason");
    try {
      const { data } = await api.post("/billing/cancel-intent", {
        reason, comment, offers_shown: OFFERS[reason] || [],
      });
      setFeedbackId(data.feedback_id);
      setMaxMonths(data.max_pause_months || 3);
      setStep(2);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    } finally {
      setBusy(null);
    }
  };

  const pause = async (months) => {
    setBusy(`pause-${months}`);
    try {
      const { data } = await api.post("/billing/pause", { months, feedback_id: feedbackId });
      toast.success(`Paused until ${String(data.paused_until).slice(0, 10)} — nothing will be charged`);
      setOpen(false);
      reset();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not pause the subscription");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel", { feedback_id: feedbackId });
      toast.success(data.access_until
        ? "Cancelled — you keep full access to the end of the period you have paid for"
        : "Cancelled");
      setOpen(false);
      reset();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not cancel the subscription");
    } finally {
      setBusy(null);
    }
  };

  const offers = OFFERS[reason] || ["pause"];

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="billing-cancel-btn" className="gap-1.5">
          <XCircle size={14} /> Cancel subscription
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="cancel-flow-dialog" className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle>Before you go — what prompted this?</DialogTitle>
              <DialogDescription>
                One question, and it genuinely changes what we build next. Nothing is cancelled yet.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 space-y-2">
              {REASONS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`cancel-reason-${key}`}
                  onClick={() => setReason(key)}
                  className={`w-full rounded-sm border p-3 text-left text-sm font-medium transition-colors ${
                    reason === key
                      ? "border-[color:var(--brand-primary)] bg-slate-50 text-slate-900"
                      : "border-slate-200 text-slate-700 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="cancel-comment"
              rows={3}
              className="mt-3"
              placeholder="Anything you want to add (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button
              data-testid="cancel-reason-continue"
              disabled={!reason || busy === "reason"}
              onClick={submitReason}
              className="mt-4 w-full gap-1.5"
              style={{ backgroundColor: "var(--brand-primary)" }}
            >
              {busy === "reason" && <Loader2 size={14} className="animate-spin" />} Continue
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="cancel-offer-title">
                {offers.includes("pause") ? "You do not have to lose the workspace" : "Let us help first"}
              </DialogTitle>
              <DialogDescription>
                Based on what you told us, one of these may suit you better than cancelling.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 space-y-3">
              {offers.map((key) => {
                const offer = OFFER_COPY[key];
                const Icon = offer.icon;
                return (
                  <div key={key} data-testid={`cancel-offer-${key}`} className="rounded-sm border border-slate-200 p-4">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Icon size={15} style={{ color: "var(--brand-primary)" }} /> {offer.title}
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{offer.body}</p>
                    {key === "pause" && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {Array.from({ length: maxMonths }, (_, i) => i + 1).map((m) => (
                          <Button
                            key={m}
                            size="sm"
                            data-testid={`cancel-pause-${m}`}
                            disabled={!!busy}
                            onClick={() => pause(m)}
                            className="gap-1.5"
                            style={{ backgroundColor: "var(--brand-primary)" }}
                          >
                            {busy === `pause-${m}` && <Loader2 size={13} className="animate-spin" />}
                            Pause {m} month{m === 1 ? "" : "s"}
                          </Button>
                        ))}
                      </div>
                    )}
                    {key === "seats" && (
                      <p className="mt-2 text-xs text-slate-500">
                        Close this and use the seat picker above, then choose your plan again.
                      </p>
                    )}
                    {key === "downgrade" && (
                      <p className="mt-2 text-xs text-slate-500">
                        Close this and pick Foundation above — the change is applied to your existing
                        subscription, not a second one.
                      </p>
                    )}
                    {key === "talk" && (
                      <a
                        href="/support"
                        data-testid="cancel-offer-talk-link"
                        className="mt-2 inline-block text-xs font-semibold underline"
                        style={{ color: "var(--brand-primary)" }}
                      >
                        Raise it with us
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="text-xs text-slate-500">
                If you cancel, you keep full access to the end of the period you have already paid
                for — nothing is deleted, and you can restore the workspace at any time.
              </p>
              <Button
                variant="outline"
                data-testid="cancel-confirm-btn"
                disabled={!!busy}
                onClick={cancel}
                className="mt-3 w-full gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                {busy === "cancel" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Cancel my subscription anyway
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
